import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import { useManualQuery } from 'graphql-hooks'
import axios from 'axios'
import { App } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { createColumnHelper, useTable } from '@tanstack/react-table'

import Api from '@vacationtracker/shared/services/api'
import { getAuthTokens } from '@vacationtracker/shared/services/auth'
import { wait } from '@vacationtracker/shared/functions/wait'
import { filterCountryOptions } from '@vacationtracker/shared/functions/filter-countries'
import { ICountry, ISubdivision, countriesForPicker, findCountry, findSubdivision, subdivisionOptions, subdivisionTermKey } from '@vacationtracker/shared/data/countries'
import { HolidayLib, IGetHolidaysResponse } from '@vacationtracker/shared/types/holidays'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'

import * as logger from '../../../../services/logger'
import { IGetLocationPlace } from '../../../../types/locations'
import { FrontendUrls } from '../../../../types/urls'
import IntlMessages from '../../../../util/IntlMessages'
import { redirectIfEntityNotFound } from '../../../../util/entity-not-found-redirect'
import { useAppSelector } from '../../../../store/hooks'
import { selectLocaleSlice } from '../../../../store/locale-slice'
import { notificationStore } from '../../../../context/notificationsContext/store'
import YesNoSwitch from '../../../../components/yes-no-switch'
import { HolidayDates, TypeTag, WithReason } from '../../../../components/location-holidays/parts'
import { libraryTag, utcToday } from '../../../../components/location-holidays/logic'
import { Banner } from '../../../../design-system/Banner'
import { Button } from '../../../../design-system/Button'
import { CheckboxField } from '../../../../design-system/CheckboxField'
import { DataGrid, dataGridFeatures } from '../../../../design-system/DataGrid'
import type { DataGridFeatures } from '../../../../design-system/DataGrid'
import { PageHeader } from '../../../../design-system/PageHeader'
import { SelectField } from '../../../../design-system/SelectField'
import { Stack } from '../../../../design-system/Stack'
import { Text } from '../../../../design-system/Text'
import {
  IExistingHoliday,
  IExistingHolidaysByYear,
  IImportPlace,
  alreadyImported,
  clashingRow,
  daySpan,
  endsSeries,
  isRepeatingCustom,
  holidaysOfYear,
  initialSelection,
  lostImportCount,
  placeChanged,
  remainingAfterImport,
  rowId,
} from './logic'

interface IImportHolidaysPage {
  match: {
    params: {
      id: string
      year?: string
    }
  }
}

interface IPlace {
  country?: ICountry
  state?: ISubdivision
}

interface IFindHolidaysParams extends IPlace {
  year: number
  locale: string
}

interface ILocationWithHolidays extends IGetLocationPlace {
  holidays?: IExistingHolidaysByYear[]
  holidaysImportPlace?: IImportPlace | null
}

const getLocationForImport = `
  query GetLocationForImport($id: String!) {
    getLocation(id: $id) {
      id
      name
      countryCode
      subdivisionCode
      holidaysImportPlace {
        country
        state
      }
      holidays {
        year
        holidays {
          id
          name
          date
          type
          thirdPartyName
          multiDayId
          recurring
          isHalfDay
        }
      }
    }
  }
`

type FetchStatus = 'loading' | 'loaded' | 'failed'

const COLUMN_WIDTHS: Record<string, number> = {
  select: 48,
  name: 260,
  date: 260,
  type: 130,
}

const columnHelper = createColumnHelper<DataGridFeatures, IGetHolidaysResponse>()

const placeOf = ({ country, state }: IImportPlace): IPlace => {
  if (!country) return {}
  return {
    country: findCountry(country),
    state: state ? findSubdivision(state, country) : undefined,
  }
}

const seedPlaceFromLocation = (location: IGetLocationPlace): IPlace => placeOf({ country: location.countryCode, state: location.subdivisionCode })

const placeName = (place: IPlace): string => {
  const country = place.country?.name ?? ''
  return place.state ? `${country}, ${place.state.name}` : country
}

const findHolidaysForCountry = async ({ year, country, state, locale }: IFindHolidaysParams): Promise<IGetHolidaysResponse[]> => {
  const tokens = await getAuthTokens()
  const response = await axios.get(`${process.env.REACT_APP_API_URL}/core/holidays`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${tokens?.idToken}`,
    },
    params: {
      year,
      countryIso: country?.iso,
      country: country?.name,
      stateIso: state?.iso,
      locale,
    },
  })
  // date-holidays returns some dates (lunar and religious calendars) with a zone suffix such as
  // "2023-02-06 00:00:00 -0600", so it is cut off.
  return (response.data as IGetHolidaysResponse[]).map((holiday) => ({ ...holiday, date: holiday.date.slice(0, 19) }))
}

const SHORT_DAY: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' }

interface ClashNoteProps {
  own: IExistingHoliday
  existing: IExistingHoliday[]
  year: number
}

const ClashNote = ({ own, existing, year }: ClashNoteProps): React.ReactElement => {
  const { formatDate } = useIntl()
  const span = daySpan(existing, own)
  const repeating = isRepeatingCustom(own)
  const ends = endsSeries(own, year, utcToday().year())
  return (
    <>
      {span ? (
        <IntlMessages id="holidaysTab.import.haveNameSpan" values={{ name: own.name, start: formatDate(span.start, SHORT_DAY), end: formatDate(span.end, SHORT_DAY) }} />
      ) : (
        <IntlMessages id={own.isHalfDay ? 'holidaysTab.import.haveNameHalf' : 'holidaysTab.import.haveName'} values={{ name: own.name }} />
      )}
      {repeating && (
        <>
          {' '}
          <IntlMessages id={ends ? 'holidaysTab.import.endsSeries' : 'holidaysTab.import.replacesYearOnly'} values={{ year }} />
        </>
      )}
    </>
  )
}

const ImportHolidaysPage = ({ match }: IImportHolidaysPage): React.ReactElement => {
  const { formatMessage } = useIntl()
  const history = useHistory()
  const { notification } = App.useApp()
  const { actionNotifications, setActionNotifications } = useContext(notificationStore)
  const { locale } = useAppSelector(selectLocaleSlice)
  const activeLocale = locale.locale as LocaleEnum
  const locationId = match.params.id
  const year = Number(match.params.year) || utcToday().year()
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const [location, setLocation] = useState<ILocationWithHolidays>()
  const [place, setPlace] = useState<IPlace>({})
  const [holidays, setHolidays] = useState<IGetHolidaysResponse[]>([])
  const [status, setStatus] = useState<FetchStatus>('loading')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [recurring, setRecurring] = useState(true)
  const [importing, setImporting] = useState(false)

  const [getLocationByIdQuery] = useManualQuery<{ getLocation: ILocationWithHolidays }, { id: string }>(getLocationForImport)

  useEffect(() => {
    void fetchLocation()
  }, [locationId])

  const fetchLocation = async () => {
    try {
      const response = await getLocationByIdQuery({ variables: { id: locationId } })
      if (!response.data || response.error) throw response.error
      if (!redirectIfEntityNotFound(response.data.getLocation, FrontendUrls.locations, history)) return
      const fetched = response.data.getLocation
      setLocation(fetched)
      setPlace(seedPlaceFromLocation(fetched))
    } catch (err) {
      logger.error('error fetching location', err)
      setStatus('failed')
    }
  }

  useEffect(() => {
    if (!location) return
    if (!place.country) {
      setHolidays([])
      setSelected(new Set())
      setStatus('loaded')
      return
    }
    let current = true
    setStatus('loading')
    findHolidaysForCountry({ ...place, year, locale: locale.locale })
      .then((found) => {
        if (!current) return
        setHolidays(found)
        const remaining = remainingAfterImport({
          existing: holidaysOfYear(location.holidays ?? [], year),
          previous: location.holidaysImportPlace,
          picked: { country: place.country?.iso, state: place.state?.iso },
          library: found,
        })
        setSelected(initialSelection(found, remaining))
        setStatus('loaded')
      })
      .catch((err) => {
        if (!current) return
        logger.error('error fetching holidays', err)
        setStatus('failed')
      })
    return () => {
      current = false
    }
  }, [location, place.country?.iso, place.state?.iso, year, locale.locale])

  const google = holidays[0]?.google === true
  const holidayLib: HolidayLib = google ? 'GOOGLE' : 'DATE_HOLIDAYS'
  const countryLabel = place.country?.name ?? ''
  const placeLabel = placeName(place)
  const ownPlace = location ? seedPlaceFromLocation(location) : {}
  const otherPlace = Boolean(ownPlace.country && place.country) && (place.country?.iso !== ownPlace.country?.iso || place.state?.iso !== ownPlace.state?.iso)
  const noPlace = Boolean(location) && !place.country
  const noCalendar = status === 'loaded' && !noPlace && holidays.length === 0
  const existing = useMemo(() => holidaysOfYear(location?.holidays ?? [], year), [location, year])
  const previousPlace = location?.holidaysImportPlace
  const pickedCountry = place.country?.iso
  const pickedState = place.state?.iso
  // A place change removes the old place's imports in every year up to next year, whatever the import itself writes.
  const lastAffectedYear = Math.max(year, utcToday().year() + 1)
  const replacing = useMemo(() => {
    const input = { existing, previous: previousPlace, picked: { country: pickedCountry, state: pickedState }, library: holidays }
    return { remaining: remainingAfterImport(input), changed: placeChanged(previousPlace, input.picked) }
  }, [existing, previousPlace, pickedCountry, pickedState, holidays])
  // The backend refreshes only twins of what is imported, so an unticked twin is lost like any other old import.
  const lostCount = useMemo(() => {
    const chosen = holidays.filter((holiday, index) => selected.has(rowId(holiday, index)))
    const affected = (location?.holidays ?? []).filter((entry) => Number(entry.year) >= year && Number(entry.year) <= lastAffectedYear).flatMap((entry) => entry.holidays)
    return lostImportCount({ existing, previous: previousPlace, picked: { country: pickedCountry, state: pickedState }, library: chosen, affected })
  }, [holidays, selected, location, year, lastAffectedYear, existing, previousPlace, pickedCountry, pickedState])
  const clashes = useMemo(
    () =>
      new Map(
        holidays.flatMap((holiday, index): [string, IExistingHoliday][] => {
          const own = clashingRow(replacing.remaining, holiday)
          return own ? [[rowId(holiday, index), own]] : []
        })
      ),
    [holidays, replacing]
  )
  // A clash note wins: a day holding both the same import and another holiday is a clash.
  const imported = useMemo(
    () => new Set(holidays.flatMap((holiday, index) => (alreadyImported(replacing.remaining, holiday) && !clashes.has(rowId(holiday, index)) ? [rowId(holiday, index)] : []))),
    [holidays, replacing, clashes]
  )
  const allChosen = holidays.length > 0 && selected.size === holidays.length
  const count = selected.size
  const tabUrl = `/app/settings/locations/${locationId}/holidays`
  const backToTab = () => history.push(tabUrl, { year })
  const countryIso = place.country?.iso
  const subdivisions = countryIso ? subdivisionOptions(countryIso) : []

  const toggle = (id: string, on: boolean) =>
    setSelected((current) => {
      const next = new Set(current)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.display({
          id: 'select',
          header: () => (
            <CheckboxField
              ariaLabel={formatMessage({ id: allChosen ? 'components.insertHolidaysForm.deselectAll' : 'components.insertHolidaysForm.selectAll' })}
              checked={allChosen}
              indeterminate={count > 0 && !allChosen}
              onChange={() => setSelected(allChosen ? new Set() : new Set(holidays.map(rowId)))}
            />
          ),
          cell: ({ row }) => {
            const own = clashes.get(row.id)
            return (
              <CheckboxField
                ariaLabel={own ? formatMessage({ id: 'holidaysTab.import.replaceLabel' }, { name: own.name }) : row.original.name}
                checked={selected.has(row.id)}
                onChange={(on) => toggle(row.id, on)}
              />
            )
          },
        }),
        columnHelper.display({
          id: 'name',
          header: () => formatMessage({ id: 'holidays.holidaysName' }),
          cell: ({ row }) => row.original.name,
        }),
        columnHelper.display({
          id: 'date',
          header: () => formatMessage({ id: 'components.leavesColumns.dates' }),
          cell: ({ row }) => {
            const own = clashes.get(row.id)
            const date = <HolidayDates row={{ date: row.original.valueForApi }} locale={activeLocale} />
            if (!own && !imported.has(row.id)) return date
            return (
              <Stack gap="xs">
                <span>{date}</span>
                <Text tone="secondary">{own ? <ClashNote own={own} existing={existing} year={year} /> : <IntlMessages id="holidaysTab.import.alreadyImported" />}</Text>
              </Stack>
            )
          },
        }),
        // Google's calendar has no types, so its rows get no Type column at all.
        ...(google
          ? []
          : [
              columnHelper.display({
                id: 'type',
                header: () => formatMessage({ id: 'app.type' }),
                cell: ({ row }) => <TypeTag tag={libraryTag(row.original.type)} />,
              }),
            ]),
      ]),
    [formatMessage, holidays, selected, allChosen, count, activeLocale, google, existing, clashes, imported]
  )

  const table = useTable({ features: dataGridFeatures, columns, data: holidays, getRowId: rowId })

  const submit = async () => {
    setImporting(true)
    const chosen = holidays.filter((holiday, index) => selected.has(rowId(holiday, index)))
    const replace = holidays.filter((holiday, index) => selected.has(rowId(holiday, index)) && clashes.has(rowId(holiday, index))).map((holiday) => holiday.name)
    try {
      const response = await Api.post('/core/event', {
        eventType: 'LOCATION_HOLIDAYS_IMPORTED',
        eventGroup: 'LOCATION',
        locationId,
        holidays: chosen.map((holiday) => ({ date: holiday.valueForApi, name: holiday.name })),
        ...(replace.length > 0 ? { replace } : {}),
        year,
        country: place.country?.iso,
        state: place.state?.iso,
        locale: locale.locale,
        // The backend imports Google's calendar into the one year only.
        recurring: google ? false : recurring,
        holidayLib,
      })
      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: 'location.holidays.updateInProgress' }),
        icon: <LoadingOutlined />,
        duration: 0,
      })
      setActionNotifications([...actionNotifications, response.correlationId])
    } catch (error) {
      logger.error('error importing holidays', error)
      notification.error({
        message: formatMessage({ id: 'error.generic' }),
        description: '',
        duration: 0,
      })
      setImporting(false)
      return
    }
    // The holidays tab reloads on arrival; this gives the import a head start so it shows the new rows.
    await wait(3000)
    if (!mounted.current) return
    setImporting(false)
    backToTab()
  }

  const noCalendarText = formatMessage({ id: 'holidaysTab.noCalendarShort' }, { country: countryLabel })
  const failed = status === 'failed'
  const loadFailedText = formatMessage({ id: 'holidaysTab.import.loadFailed' })
  const submitReason = failed
    ? loadFailedText
    : noPlace
      ? formatMessage({ id: 'holidaysTab.import.chooseCountry' })
      : noCalendar
        ? noCalendarText
        : count === 0
          ? formatMessage({ id: 'holidaysTab.import.noneChosen' })
          : undefined

  return (
    <div className="main-content">
      <PageHeader
        title={<IntlMessages id="holidaysTab.import.title" values={{ year }} />}
        parent={[
          { label: <IntlMessages id="app.locations" />, to: FrontendUrls.locations },
          { label: location?.name, to: tabUrl },
        ]}
      />
      <div className="main-content-body">
        <Stack gap="lg">
          {location && (
            <Stack gap="xs">
              <div style={{ maxWidth: 640 }}>
                <Stack direction="horizontal" gap="base" align="flex-end" style={{ width: '100%' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <SelectField
                      label={<IntlMessages id="app.country" />}
                      requirement="required"
                      value={countryIso}
                      placeholder={formatMessage({ id: 'locationPlace.country.placeholder' })}
                      options={countriesForPicker().map((item) => ({ value: item.iso, label: item.name }))}
                      filterOption={filterCountryOptions}
                      onChange={(iso?: string) => setPlace(iso ? { country: findCountry(iso) } : {})}
                    />
                  </div>
                  {countryIso && subdivisions.length > 0 && (
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <SelectField
                        label={<IntlMessages id={subdivisionTermKey(countryIso)} />}
                        requirement="optional"
                        clearable
                        value={place.state?.iso}
                        placeholder={formatMessage({ id: subdivisionTermKey(countryIso) })}
                        options={subdivisions}
                        onChange={(iso?: string) => setPlace({ country: place.country, state: iso ? findSubdivision(iso, countryIso) : undefined })}
                      />
                    </div>
                  )}
                </Stack>
              </div>
              <Text tone="secondary">
                <IntlMessages id="holidaysTab.import.intro" />
              </Text>
              {imported.size > 0 && (
                <Text tone="secondary">
                  <IntlMessages id="holidaysTab.import.alreadyImportedCount" values={{ count: imported.size }} />
                </Text>
              )}
              {otherPlace && (
                <Text tone="secondary">
                  <IntlMessages id="holidaysTab.import.ownPlace" values={{ location: location.name, place: placeName(ownPlace) }} />
                </Text>
              )}
              {google && (
                <Text tone="secondary">
                  <IntlMessages id="holidaysTab.import.sourceGoogle" values={{ place: placeLabel }} />
                </Text>
              )}
            </Stack>
          )}

          {previousPlace && replacing.changed && lostCount > 0 && (
            <Banner
              tone="info"
              message={formatMessage({ id: 'holidaysTab.import.replacesPlace' }, { count: lostCount, place: placeLabel, previous: placeName(placeOf(previousPlace)) })}
            />
          )}

          {failed ? (
            <Banner tone="danger" message={loadFailedText} />
          ) : noPlace ? null : noCalendar ? (
            <Text>{noCalendarText}</Text>
          ) : (
            <DataGrid<IGetHolidaysResponse>
              table={table}
              loading={status === 'loading'}
              columnWidths={COLUMN_WIDTHS}
              chromeColumnIds={google ? ['select'] : ['select', 'type']}
              flexColumnIds={['name', 'date', 'type']}
              wrapColumnIds={['date']}
              separators="uniform"
            />
          )}

          {!failed && !noPlace && !noCalendar && !google && (
            <YesNoSwitch required={false} defaultValue={recurring} onChange={setRecurring} label={<IntlMessages id="holidaysTab.repeatYearly" />} />
          )}

          <Stack direction="horizontal" gap="sm">
            <Button variant="outline" onClick={backToTab}>
              <IntlMessages id="app.cancel" />
            </Button>
            <WithReason reason={submitReason}>
              <Button variant="primary" disabled={Boolean(submitReason) || status !== 'loaded'} loading={importing} onClick={() => void submit()}>
                {noPlace || noCalendar ? <IntlMessages id="components.insertHolidaysForm.insertHolidays" /> : <IntlMessages id="holidaysTab.import.button" values={{ count }} />}
              </Button>
            </WithReason>
          </Stack>
        </Stack>
      </div>
    </div>
  )
}

export default ImportHolidaysPage
